import iconProcessing from '@/assets/images/status-icons/status-processing.svg'
import iconPending    from '@/assets/images/status-icons/status-pending.svg'
import iconShipped    from '@/assets/images/status-icons/status-shipped.svg'
import iconCompleted  from '@/assets/images/status-icons/status-completed.svg'
import iconDisputed   from '@/assets/images/status-icons/exclamation-disputed.svg'
import iconRefunded   from '@/assets/images/status-icons/status-refunded.svg'
import iconCanceled   from '@/assets/images/status-icons/status-canceled.svg'

// Shared constants for the Orders feature (page, filters, and detail panel).
// Anything that's hard-coded into UI option lists, used to seed/validate URL
// query params, or maps a column field to an API sort key lives here so the
// view and its filter components stay in sync without duplicate literals.

// ─── Default filter values ───────────────────────────────────────────────────
export const DEFAULT_DATE_RANGE  = 'last_30_days'
export const DEFAULT_SORT_PRESET = 'newest'
export const DEFAULT_PAGE        = 1
export const DEFAULT_PER_PAGE    = 25

// ─── Date range presets ──────────────────────────────────────────────────────
// `short` is what we show on mobile (< md) where the trigger button is tight;
// `label` is the dropdown row text and the desktop button copy.
export const DATE_RANGE_PRESETS = [
  { value: 'yesterday',    label: 'Yesterday',    short: 'Yesterday' },
  { value: 'last_30_days', label: 'Last 30 days', short: 'Last 30d'  },
  { value: 'last_60_days', label: 'Last 60 days', short: 'Last 60d'  },
  { value: 'last_90_days', label: 'Last 90 days', short: 'Last 90d'  },
]

export const DATE_RANGE_VALUES = DATE_RANGE_PRESETS.map(p => p.value)

// ─── Marketplaces ────────────────────────────────────────────────────────────
// User-pickable marketplaces in the toolbar filter dropdown.
export const ORDER_MARKETPLACES = [
  { value: 'whatnot', label: 'Whatnot' },
  { value: 'collx',   label: 'CollX'   },
  { value: 'ebay',    label: 'eBay'    },
  { value: 'shopify', label: 'Shopify' },
]

export const ORDER_MARKETPLACE_VALUES = ORDER_MARKETPLACES.map(m => m.value)

// API also accepts `other` (catch-all for non-listed marketplaces) — kept
// for URL-restore validation but not rendered as a filter option.
export const API_MARKETPLACE_VALUES = [...ORDER_MARKETPLACE_VALUES, 'other']

// ─── Toolbar sort presets ────────────────────────────────────────────────────
export const ORDER_SORT_PRESETS = [
  { value: 'newest', label: 'Newest first', icon: 'heroicons:bars-arrow-down' },
  { value: 'oldest', label: 'Oldest first', icon: 'heroicons:bars-arrow-up'   },
]

export const ORDER_SORT_PRESET_VALUES = ORDER_SORT_PRESETS.map(p => p.value)

// ─── API sort keys ───────────────────────────────────────────────────────────
// The four values `/sales-orders` accepts on `sort`. Anything else is dropped
// before sending and the toolbar preset takes over.
export const API_SORT_KEYS = ['sold_at', 'quantity', 'gross_amount', 'picked_at']

// AG Grid column field → API `sort` value. Columns absent from this map don't
// trigger a server sort (the toolbar preset stays in effect).
export const COLUMN_TO_API_SORT = {
  sold_at:    'sold_at',
  unit_price: 'gross_amount',
}

// ─── Order status pill metadata ─────────────────────────────────────────────
// Used by `OrdersStatusCell` (table) and `OrderDetailOverview` (panel) so the
// label + colour + icon for any status string only needs to change in one
// place. Backend normalises common statuses to spaces (e.g. "Ready to Ship");
// the keys here are lowercase + underscore so `resolveOrderStatus` can do a
// single transformation. British "cancelled" / US "canceled" both map.
export const ORDER_STATUS_MAP = {
  processing:    { label: 'Processing',    img: iconPending,    variant: 'gray'   },
  ready_to_ship: { label: 'Ready to Ship', img: iconProcessing, variant: 'yellow' },
  // "Sold" mirrors "Ready to Ship" visually — same icon + yellow variant. Real
  // BE data started returning this status alongside the documented ones.
  sold:          { label: 'Sold',          img: iconProcessing, variant: 'yellow' },
  shipped:       { label: 'Shipped',       img: iconShipped,    variant: 'blue'   },
  completed:     { label: 'Completed',     img: iconCompleted,  variant: 'green'  },
  disputed:      { label: 'Disputed',      img: iconDisputed,   variant: 'red'    },
  refunded:      { label: 'Refunded',      img: iconRefunded,   variant: 'pink'   },
  canceled:      { label: 'Canceled',      img: iconCanceled,   variant: 'muted'  },
  cancelled:     { label: 'Cancelled',     img: iconCanceled,   variant: 'muted'  },
}

// Unknown statuses still get a neutral pending icon so the pill never renders
// icon-less in real data — only the raw label changes.
export const ORDER_STATUS_FALLBACK = { label: '—', img: iconPending, variant: 'gray' }

// ─── Chart series (Orders by Marketplace) ───────────────────────────────────
// `color` is a raw hex string because Unovis renders SVG and needs a literal
// — Tailwind classes can't be applied to a `fill`/`stroke` attribute. Kept
// here (vs. inline in the view) so a future palette swap is one edit, and so
// the values can be reused by any other chart consumer that displays the
// same per-marketplace breakdown. Hex equivalents follow Tailwind's palette:
//
//   collx   → tint-1     (#0086FF) — brand blue
//   ebay    → orange-400 (#FB923C)
//   shopify → green-500  (#22C55E)
//   whatnot → gray-900   (#111827)
export const ORDERS_CHART_SERIES = [
  { key: 'collx',   label: 'CollX',   color: '#0086FF' },
  { key: 'ebay',    label: 'eBay',    color: '#FB923C' },
  { key: 'shopify', label: 'Shopify', color: '#22C55E' },
  { key: 'whatnot', label: 'Whatnot', color: '#111827' },
]
